'use client';

import { FC, ReactElement, ReactNode } from 'react';
import Link from 'next/link';
import classNames from 'classnames';
import { motion, MotionConfig } from 'motion/react';
import { useTranslations } from 'next-intl';
import { appRoutes } from 'config';
import { buildSpacePath } from 'config/routing';
import { usePathname } from 'core/i18n/navigation';
import { useActiveSpace } from 'xstate/hooks/useActiveSpace';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faMoon,
  faSunBright,
  faUser,
  faMagnifyingGlass,
  faCrown,
} from '@fortawesome/pro-solid-svg-icons';
import MemoIcon from 'assets/icons/icon-memo.svg';
import BellIcon from 'assets/icons/icon-bell.svg';
import RailUserMenu from './RailUserMenu';
import { useProStatus } from 'networking/revenueCat/hooks/useProStatus';
import { useTheme } from 'hooks/useTheme';
import { useUnseenNotificationsCount } from 'networking/mindfeed/hooks/notifications/useNotifications';

type IconRenderer = (className: string) => ReactNode;

const svg =
  (Icon: FC<{ className?: string }>): IconRenderer =>
  (className) => <Icon className={className} />;

const fa =
  (icon: IconProp): IconRenderer =>
  (className) => <FontAwesomeIcon icon={icon} className={className} />;

type RailItemProps = {
  icon: IconRenderer;
  label?: string;
  selected?: boolean;
  href?: string;
  onClick?: () => void;
  className?: string;
  /** 'rail' is the desktop side rail. 'bar' is the mobile bottom tab bar. */
  variant?: 'rail' | 'bar';
  /** Overrides the icon colour (e.g. the Pro crown); defaults to the selected/idle tint. */
  iconColorClassName?: string;
  /** A small red count on the icon's corner, e.g. new notifications. */
  badge?: string;
  'aria-label': string;
};

const RailItem = ({
  icon,
  label,
  selected = false,
  href,
  onClick,
  variant = 'rail',
  className: extraClassName,
  iconColorClassName,
  badge,
  ...rest
}: RailItemProps): ReactElement => {
  const s = variant === 'bar' ? styles.barItem : styles.railItem;
  const content = (
    <>
      <span
        className={classNames(
          s.iconWrap,
          selected ? s.iconWrapSelected : s.iconWrapIdle,
        )}
      >
        {/* The white selection tile. One shared layoutId, so when the tab
            changes it glides from the old tile to the new one instead of
            blinking. The rail stays mounted across pages (SiteChrome), which
            is what lets it travel between routes. */}
        {variant === 'rail' && selected && (
          <motion.span
            layoutId="rail-selection"
            className={styles.railItem.selection}
            transition={{ type: 'spring', stiffness: 520, damping: 38 }}
            aria-hidden
          />
        )}
        {icon(
          classNames(
            s.icon,
            iconColorClassName ?? (selected ? s.iconSelected : s.iconIdle),
          ),
        )}
        {badge && (
          <span className={styles.badge} aria-hidden>
            {badge}
          </span>
        )}
      </span>
      {label && (
        <span
          className={classNames(
            s.label,
            selected ? s.labelSelected : s.labelIdle,
          )}
        >
          {label}
        </span>
      )}
    </>
  );

  const className = classNames(s.container, extraClassName);

  return href ? (
    <Link href={href} className={className} aria-label={rest['aria-label']}>
      {content}
    </Link>
  ) : (
    <button
      type="button"
      onClick={onClick}
      className={className}
      aria-label={rest['aria-label']}
    >
      {content}
    </button>
  );
};

const ActivityRail: FC = (): ReactElement => {
  const pathname = usePathname();
  const { isPro } = useProStatus();
  const { selectedSpace } = useActiveSpace();
  const { resolvedTheme, setTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const onSearch = pathname.endsWith('/search');
  const onMemos = pathname.startsWith(appRoutes.MEMORY_SPACE) && !onSearch;
  const onNotifications = pathname.startsWith(appRoutes.NOTIFICATIONS);
  const onProfile = pathname.startsWith(appRoutes.SETTINGS.DEFAULT);
  const t = useTranslations();
  const notificationsBadge = useNotificationsBadge();
  const searchHref = selectedSpace
    ? buildSpacePath({ kind: 'search', spaceId: selectedSpace.id })
    : appRoutes.MEMORY_SPACE;

  return (
    <>
      {/* Desktop: fixed vertical rail on the left. */}
      <MotionConfig reducedMotion="user">
        {/* layoutScroll: the rail is position: fixed, so the selection tile
            must be measured against the viewport, not the page. Without it,
            when a navigation changes the window scroll (a shorter page snaps
            back to the top), the tile thinks it moved by that much and
            flies in from below. */}
        <motion.nav
          layoutScroll
          className={styles.rail.container}
          aria-label={t('rail.navigation')}
        >
          <div className={styles.rail.group}>
            <RailItem
              icon={svg(MemoIcon)}
              label={t('rail.memos')}
              href={appRoutes.MEMORY_SPACE}
              selected={onMemos}
              aria-label={t('rail.memos')}
            />
            <RailItem
              icon={fa(faMagnifyingGlass)}
              label={t('search.title')}
              href={searchHref}
              selected={onSearch}
              aria-label={t('search.title')}
            />
            <RailItem
              icon={svg(BellIcon)}
              // Short caption so it fits the rail; screen readers still hear
              // the full word through aria-label.
              label={t('rail.notificationsShort')}
              href={appRoutes.NOTIFICATIONS}
              selected={onNotifications}
              badge={notificationsBadge}
              aria-label={t('pages.notifications.title')}
            />
            {/* Selected on every settings page; opens the profile page. */}
            <RailItem
              icon={fa(faUser)}
              label={t('accountMenu.profile')}
              href={appRoutes.SETTINGS.PROFILE}
              selected={onProfile}
              aria-label={t('accountMenu.profile')}
            />
          </div>

          <div className={classNames(styles.rail.group, 'mt-auto')}>
            <RailItem
              icon={fa(faCrown)}
              href={appRoutes.SETTINGS.SUBSCRIPTION}
              iconColorClassName={isPro ? 'text-mf-orange' : undefined}
              aria-label={t('accountMenu.pro')}
            />
            {/* Re-enable when feature implemented */}
            {/* <RailItem icon={fa(faKeyboard)} aria-label="Keyboard shortcuts" /> */}
            <RailItem
              icon={fa(isDark ? faSunBright : faMoon)}
              onClick={() => setTheme(isDark ? 'light' : 'dark')}
              aria-label={isDark ? t('rail.lightMode') : t('rail.darkMode')}
            />
            {/* Account menu — only below lg, where the header hides the profile. */}
            <div className="lg:hidden pt-1">
              <RailUserMenu />
            </div>
          </div>
        </motion.nav>
      </MotionConfig>

      {/* The rounded corner where the page canvas meets the header and this
          rail. Both halves live in this one fixed layer, so the corner can't
          drift on scroll or overscroll. `z-auto` and early in the DOM: any
          panel a screen pins to this corner with the same radius (the Memory
          Boxes column, the switcher bar, the settings nav) paints over it and
          supplies its own corner instead. */}
      <div className={styles.corner.navy} aria-hidden="true">
        <div className={styles.corner.canvas} />
      </div>

      {/* Mobile: bottom tab bar, like a native app. */}
      <nav className={styles.bar.container} aria-label={t('rail.navigation')}>
        <RailItem
          variant="bar"
          icon={svg(MemoIcon)}
          label={t('rail.memos')}
          href={appRoutes.MEMORY_SPACE}
          selected={onMemos}
          aria-label={t('rail.memos')}
        />
        <RailItem
          variant="bar"
          icon={svg(BellIcon)}
          label={t('pages.notifications.title')}
          href={appRoutes.NOTIFICATIONS}
          selected={onNotifications}
          badge={notificationsBadge}
          aria-label={t('pages.notifications.title')}
        />
        <RailItem
          variant="bar"
          icon={fa(faUser)}
          label={t('accountMenu.profile')}
          href={appRoutes.SETTINGS.PROFILE}
          selected={onProfile}
          aria-label={t('accountMenu.profile')}
        />
        <RailItem
          variant="bar"
          icon={fa(faMagnifyingGlass)}
          label={t('search.title')}
          href={searchHref}
          selected={onSearch}
          aria-label={t('search.title')}
        />
      </nav>
    </>
  );
};

// How many notifications arrived since the user last opened the screen, as
// on the Mac rail. Opening the screen clears it.
const useNotificationsBadge = (): string | undefined => {
  const { data: count = 0 } = useUnseenNotificationsCount();
  if (count <= 0) {
    return undefined;
  }
  return count > 99 ? '99+' : String(count);
};

export default ActivityRail;

const styles = {
  // Above the white selection tile, which is drawn inside the same wrapper.
  badge: classNames(
    'absolute -top-[5px] -right-[5px] z-10 flex h-4 min-w-4 items-center justify-center',
    'rounded-full bg-[#FF3B30] px-1 text-[10px] leading-none font-bold text-white',
  ),
  // Desktop: vertical rail pinned to the left, below the header.
  rail: {
    container: classNames(
      'hidden sm:flex fixed left-0 top-16 bottom-0 z-20 w-18 md:w-20 flex-col items-center',
      'bg-mf-blue-800 py-4 text-white',
      // One cursor over the whole rail, gaps included, so it doesn't flick
      // between the arrow and the hand while the pointer moves down it.
      'cursor-pointer [&_*]:cursor-pointer',
    ),
    // No gap: the tabs' hit areas touch, so there's no dead strip between
    // them. The spacing comes from each tab's own padding instead.
    group: 'flex flex-col items-center w-full',
  },
  // 16px square at the rail's top-right, matching `rounded-tl-2xl`'s 1rem
  // radius: the canvas quarter-disc leaves exactly the corner sliver navy.
  corner: {
    navy: classNames(
      'hidden sm:block fixed top-16 left-18 md:left-20 size-4',
      'bg-mf-blue-800 pointer-events-none',
    ),
    canvas: 'size-full rounded-tl-2xl bg-surface-canvas',
  },
  // Mobile: horizontal tab bar pinned to the bottom (respects the safe area).
  bar: {
    container: classNames(
      'sm:hidden fixed inset-x-0 bottom-0 z-20 flex flex-row items-stretch',
      'h-16 pb-[env(safe-area-inset-bottom)] rounded-t-3xl',
      'bg-white shadow-[0_-4px_16px_rgba(0,0,0,0.06)]',
    ),
  },
  // Desktop rail item: an icon tile with a caption below it. Every tab has
  // a faint ghost tile, so icons and captions keep the same rhythm down the
  // rail. The current tab's tile is covered by the white selection, the
  // panels' white, finished like a key (faint tint, hairline edge, soft
  // shadow) and framed by the header avatar's ring. It slides between tabs;
  // icons lift slightly on hover and press in on click.
  railItem: {
    container:
      'group flex flex-col items-center justify-center gap-2 w-full py-2 cursor-pointer focus:outline-none',
    iconWrap: classNames(
      'relative flex items-center justify-center w-11 h-11 md:w-[52px] md:h-[52px] rounded-[14px]',
      'transition-[background-color,transform] duration-200 ease-out',
      'group-active:scale-[0.94]',
    ),
    iconWrapSelected: '',
    iconWrapIdle: 'bg-white/[0.06] group-hover:bg-white/[0.12]',
    selection: classNames(
      'absolute inset-0 rounded-[14px]',
      'bg-linear-to-b from-white to-[#E8EEF9]',
      'outline-[1.5px] outline-offset-[3px] outline-white/35',
      'ring-1 ring-inset ring-mf-blue-800/10',
      'shadow-[inset_0_-1px_0_rgba(19,55,117,0.08),0_6px_14px_-4px_rgba(7,35,71,0.55)]',
    ),
    icon: classNames(
      'relative w-6 h-6 md:w-7 md:h-7',
      'transition-[color,transform] duration-200 ease-out',
    ),
    iconSelected: 'text-mf-blue-800',
    iconIdle:
      'text-white/70 group-hover:text-white group-hover:-translate-y-px',
    label:
      'text-[11px] leading-none tracking-[0.01em] transition-colors duration-200',
    labelSelected: 'text-white font-semibold',
    labelIdle: 'text-white/70 font-medium group-hover:text-white',
  },
  // Mobile tab bar item. A compact icon with a pill highlight and a caption.
  barItem: {
    container:
      'group flex flex-1 flex-col items-center justify-center gap-1 h-full focus:outline-none',
    iconWrap:
      'flex items-center justify-center h-8 w-14 rounded-full transition-colors',
    iconWrapSelected: 'bg-mf-blue-50',
    iconWrapIdle: 'group-hover:bg-gray-100 group-active:bg-gray-200',
    icon: 'w-6 h-6',
    iconSelected: 'text-mf-blue',
    iconIdle: 'text-mf-off-black-500 group-hover:text-mf-off-black-600',
    label: 'text-[11px] leading-none font-medium transition-colors',
    labelSelected: 'text-mf-blue',
    labelIdle: 'text-mf-off-black-500 group-hover:text-mf-off-black-600',
  },
};
